import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenantId } from './useTenantId';
import { toast } from 'sonner';

export interface TradeSpecialty {
  id: string;
  name: string;
  is_active: boolean;
}

export interface DocumentRequirements {
  // Common requirements
  min_days_until_expiry?: number;
  max_age_days?: number;
  
  // Insurance requirements
  min_per_occurrence?: number;
  min_aggregate?: number;
  additional_insured_required?: boolean;
  waiver_of_subrogation_required?: boolean;
  primary_noncontributory_required?: boolean;
  min_am_best_rating?: string;
  admitted_carrier_required?: boolean;
  
  // License requirements
  active_status_required?: boolean;
  min_monetary_limit?: number;
  
  // Financial requirements (bonding)
  min_single_project_limit?: number;
  min_aggregate_capacity?: number;
  
  // Safety requirements
  max_emr?: number;
  
  // Legal/Tax requirements
  tin_required?: boolean;
}

export interface DocumentType {
  id: string;
  name: string;
  category: string;
  required_for_prequalification: boolean;
  has_expiry: boolean;
  verification_required: boolean;
  verification_roles: string[];
  reminder_days_before_expiry: number | null;
  requirements?: DocumentRequirements;
}

export interface RatingWeights {
  safety_weight: number;
  quality_weight: number;
  reliability_weight: number;
}

export interface ContractSettings {
  notification_days_before_expiry: number[];
  notify_on_contract_upload: boolean;
  notify_on_contract_expiry: boolean;
  expiry_alert_recipients: string[];
}

export interface PartnerSettings {
  rating_weights: RatingWeights;
  trade_specialties: TradeSpecialty[];
  document_types: DocumentType[];
  field_config: {
    contractor: {
      enabled_fields: string[];
      required_fields: string[];
    };
    vendor: {
      enabled_fields: string[];
      required_fields: string[];
    };
  };
  prequalification_settings: {
    expiry_days: number;
    reminder_days_before_expiry: number[];
    auto_expire_on_document_expiry: boolean;
    require_annual_update: boolean;
  };
  notification_settings: {
    notify_on_document_upload: boolean;
    notify_on_prequalification_approval: boolean;
    notify_on_expiry_alerts: boolean;
    expiry_alert_recipients: string[];
  };
  contract_settings?: ContractSettings;
}

export function usePartnerSettings() {
  const { tenantId } = useTenantId();
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['partner-settings', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('settings')
        .eq('id', tenantId)
        .single();

      if (error) throw error;
      const settingsData = data.settings as any;
      return (settingsData?.partner_settings || {}) as PartnerSettings;
    },
    enabled: !!tenantId,
  });

  const updateSettings = useMutation({
    mutationFn: async (newSettings: Partial<PartnerSettings>) => {
      // First fetch current settings
      const { data: currentData } = await supabase
        .from('tenants')
        .select('settings')
        .eq('id', tenantId)
        .single();

      const currentSettings = (currentData?.settings as any) || {};

      const { error } = await supabase
        .from('tenants')
        .update({
          settings: {
            ...currentSettings,
            partner_settings: {
              ...(currentSettings.partner_settings || {}),
              ...newSettings,
            },
          } as any,
        })
        .eq('id', tenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-settings'] });
      toast.success('Settings updated successfully');
    },
    onError: (error) => {
      console.error('Error updating partner settings:', error);
      toast.error('Failed to update settings');
    },
  });

  return {
    settings,
    isLoading,
    updateSettings: updateSettings.mutateAsync,
  };
}